import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MenuComponent } from '../../../shared/components/menu/menu.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { ModuloEnum } from '../../../shared/enums/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/funcionalidade.enum';
import { PermissaoEnum } from '../../../shared/enums/permissao.enum';
import { IbptOrigemExecucaoEnum } from '../../../shared/enums/motor-fiscal/ibpt-origem-execucao.enum';
import { IbptResultadoExecucaoEnum } from '../../../shared/enums/motor-fiscal/ibpt-resultado-execucao.enum';
import { IbptEtapaExecucaoEnum } from '../../../shared/enums/motor-fiscal/ibpt-etapa-execucao.enum';
import { IbptMotivoFalhaExecucaoEnum } from '../../../shared/enums/motor-fiscal/ibpt-motivo-falha-execucao.enum';
import { IbptStatusConexaoEnum } from '../../../shared/enums/motor-fiscal/ibpt-status-conexao.enum';
import { IbptStatusTabelaEnum } from '../../../shared/enums/motor-fiscal/ibpt-status-tabela.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { ReturnStruct } from '../../../shared/services/structs/return.struct';
import { TabelaIbptService } from '../../../shared/services/apis/motor-fiscal/tabela-ibpt.service';
import { ConfiguracaoIbptResponse } from '../../../shared/services/responses/motor-fiscal/configuracao-ibpt.response';
import { TestarConexaoIbptResponse } from '../../../shared/services/responses/motor-fiscal/testar-conexao-ibpt.response';
import { SituacaoTabelaIbptResponse } from '../../../shared/services/responses/motor-fiscal/situacao-tabela-ibpt.response';
import { TemPermissaoDirective } from '../../../shared/directives/habilita-por-permissao.directive';
import { AuthService } from '../../../shared/services/apis/usuario-admin/auth.service';
import { UsuarioLookupService } from '../../../shared/services/apis/usuario/usuario-lookup.service';
import { UsuarioListaLookupsResponse } from '../../../shared/services/responses/usuario/usuario-lista-lookups.response';
import { UnidadeFederativa } from '../../../shared/services/models/usuario/unidade-federativa.model';
import {
  AlterarCredencialIbptDialogComponent,
  AlterarCredencialIbptDialogData,
} from './alterar-credencial-ibpt-dialog/alterar-credencial-ibpt-dialog.component';
import { MapaUfIbptComponent } from './mapa-uf-ibpt/mapa-uf-ibpt.component';
import {
  DestinatarioIbptDialogComponent,
  DestinatarioIbptDialogData,
} from './destinatario-ibpt-dialog/destinatario-ibpt-dialog.component';
import {
  ImportarPlanilhaIbptDialogComponent,
  ImportarPlanilhaIbptDialogResult,
} from './importar-planilha-ibpt-dialog/importar-planilha-ibpt-dialog.component';
import {
  DetalheExecucaoIbptDialogComponent,
  DetalheExecucaoIbptDialogData,
} from './detalhe-execucao-ibpt-dialog/detalhe-execucao-ibpt-dialog.component';
import { ExecucaoIbptStruct } from '../../../shared/services/structs/motor-fiscal/execucao-ibpt.struct';
import { DetalheExecucaoIbptResponse } from '../../../shared/services/responses/motor-fiscal/detalhe-execucao-ibpt.response';
import { ListaExecucaoIbptResponse } from '../../../shared/services/responses/motor-fiscal/lista-execucao-ibpt.response';

type OpcaoPeriodoHistorico = {
  quantidadeDias: number | null;
  descricao: string;
};

@Component({
  selector: 'app-tabela-ibpt',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
    MenuComponent,
    EmptyListComponent,
    LoadingBoxComponent,
    TemPermissaoDirective,
    MapaUfIbptComponent,
  ],
  templateUrl: './tabela-ibpt.component.html',
  styleUrl: './tabela-ibpt.component.css',
})
export class TabelaIbptComponent implements OnInit, OnDestroy {
  public moduloEnum: ModuloEnum = ModuloEnum.Desenvolvedor;
  public funcionalidadeEnum: FuncionalidadeEnum = FuncionalidadeEnum.Desenvolvedor_TabelaIbpt;
  public permissaoEnum = PermissaoEnum;
  public statusTabelaEnum = IbptStatusTabelaEnum;
  public statusConexaoEnum = IbptStatusConexaoEnum;
  public resultadoExecucaoEnum = IbptResultadoExecucaoEnum;
  public origemExecucaoEnum = IbptOrigemExecucaoEnum;
  public motivoFalhaEnum = IbptMotivoFalhaExecucaoEnum;
  public etapaExecucaoEnum = IbptEtapaExecucaoEnum;

  // Nula até a primeira carga: o card mostra o loading.
  public situacao: SituacaoTabelaIbptResponse | null = null;
  // Mapa do card sem execução: UFs carregadas na versão de referência.
  public situacaoPorUfCarregada: Record<string, 'carregada'> = {};
  // Mapa do card com execução em andamento: andamento da carga pela API sobre as UFs já carregadas.
  public situacaoPorUfAndamento: Record<string, 'consultada' | 'emConsulta'> = {};
  // Lookup de UF da API de Usuário: os contratos trazem o id e a tela mostra a sigla. Vazia até carregar.
  public listaUnidadeFederativa: UnidadeFederativa[] = [];
  public configuracao: ConfiguracaoIbptResponse | null = null;
  // Status "Testando" existe só na tela, enquanto o teste de conexão aguarda a resposta.
  public isTestandoConexao: boolean = false;
  public isSolicitandoAtualizacao: boolean = false;
  public isSolicitandoCancelamento: boolean = false;
  // Execução cujo detalhe está sendo buscado; trava os demais cliques até o diálogo abrir ou a busca falhar.
  public idIbptExecucaoCarregandoDetalhe: number | null = null;
  public cnpjFormatado: string = '';
  public diasParaVencer: number | null = null;
  // Com a tabela Atualizando, a situação é consultada a cada 10 s até a execução terminar ou a tela fechar.
  private intervaloSituacao: ReturnType<typeof setInterval> | null = null;
  // Resposta que chega depois de sair da tela não pode religar a consulta automática.
  private isTelaEncerrada: boolean = false;
  // Execução em andamento vista pela tela; ao terminar, se for manual, a tela mostra a mensagem final.
  private idIbptExecucaoAcompanhada: number | null = null;
  // Links do e-mail: ?importar=1 abre a importação e ?historico=1 rola até o histórico depois da primeira situação.
  private isAbrirImportacaoAoCarregar: boolean = false;
  private isRolarHistoricoAoCarregar: boolean = false;
  // Link do e-mail ?execucao={id}: o detalhe abre depois do lookup de UF, para o mapa e as siglas virem preenchidos.
  private idIbptExecucaoAbrirAoCarregar: number | null = null;

  public readonly listaPeriodo: OpcaoPeriodoHistorico[] = [
    { quantidadeDias: 7, descricao: 'Últimos 7 dias' },
    { quantidadeDias: 30, descricao: 'Últimos 30 dias' },
    { quantidadeDias: 90, descricao: 'Últimos 90 dias' },
    { quantidadeDias: null, descricao: 'Todo o período' },
  ];
  public filtroPeriodo: number | null = 30;
  public filtroOrigem: IbptOrigemExecucaoEnum | null = null;
  public filtroResultado: IbptResultadoExecucaoEnum | null = null;

  public displayedColumns: string[] = [
    'datetimeInicio',
    'origem',
    'responsavel',
    'versao',
    'resultado',
    'detalhe',
    'duracao',
    'acao',
  ];
  public listaExecucaoExibida: ExecucaoIbptStruct[] = [];
  public isUltimaPagina: boolean = true;
  public isCarregandoHistorico: boolean = false;
  // Filtros aplicados no último "Pesquisar"; "Ver mais" e a recarga usam estes, não os da tela.
  private quantidadeDiasAplicado: number | null = 30;
  private origemAplicada: IbptOrigemExecucaoEnum | null = null;
  private resultadoAplicado: IbptResultadoExecucaoEnum | null = null;
  private paginaAtual: number = 0;

  constructor(
    private tabelaIbptService: TabelaIbptService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private authService: AuthService,
    private usuarioLookupService: UsuarioLookupService
  ) {}

  ngOnInit(): void {
    const queryParamMap = this.route.snapshot.queryParamMap;
    this.isAbrirImportacaoAoCarregar = queryParamMap.get('importar') === '1';
    this.isRolarHistoricoAoCarregar = queryParamMap.get('historico') === '1';
    const idIbptExecucao = Number(queryParamMap.get('execucao'));
    if (Number.isInteger(idIbptExecucao) && idIbptExecucao > 0) {
      this.idIbptExecucaoAbrirAoCarregar = idIbptExecucao;
    }
    this.obterUnidadeFederativa();
    this.obterConfiguracao();
    this.obterSituacao();
    this.pesquisar();
  }

  ngOnDestroy(): void {
    this.isTelaEncerrada = true;
    this.pararConsultaSituacao();
  }

  private obterConfiguracao(): void {
    this.tabelaIbptService.obterConfiguracao().subscribe({
      next: (configuracaoIbptResponse: ConfiguracaoIbptResponse) => {
        if (configuracaoIbptResponse.isError) {
          this.alertService.show('Erro', configuracaoIbptResponse.errorDescription, AlertType.error);
          return;
        }

        configuracaoIbptResponse.datetimeUltimoTeste = this.lerDataUtc(configuracaoIbptResponse.datetimeUltimoTeste);
        configuracaoIbptResponse.datetimeAlteracaoCredencial = this.lerDataUtc(configuracaoIbptResponse.datetimeAlteracaoCredencial);
        configuracaoIbptResponse.datetimeAlteracaoDestinatario = this.lerDataUtc(configuracaoIbptResponse.datetimeAlteracaoDestinatario);
        this.configuracao = configuracaoIbptResponse;
        this.cnpjFormatado = this.formatarCnpj(configuracaoIbptResponse.cnpj);
      },
      error: (error: any) => {
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  private obterUnidadeFederativa(): void {
    this.usuarioLookupService.get().subscribe({
      next: (usuarioListaLookupsResponse: UsuarioListaLookupsResponse) => {
        if (usuarioListaLookupsResponse.isError) {
          this.alertService.show('Erro', usuarioListaLookupsResponse.errorDescription, AlertType.error);
          return;
        }

        this.listaUnidadeFederativa = usuarioListaLookupsResponse.listaUnidadeFederativa;
        // A situação pode ter chegado antes: o mapa é montado de novo com as siglas.
        this.montarMapaUf();

        if (this.idIbptExecucaoAbrirAoCarregar !== null) {
          this.abrirDetalheExecucao(this.idIbptExecucaoAbrirAoCarregar);
          this.idIbptExecucaoAbrirAoCarregar = null;
        }
      },
      error: (error: any) => {
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  // Sigla da UF pelo id do lookup; "—" enquanto o lookup não carregou.
  public siglaUf(idUnidadeFederativa: number | null): string {
    return this.listaUnidadeFederativa.find(uf => uf.idUnidadeFederativa === idUnidadeFederativa)?.uf ?? '—';
  }

  private obterSituacao(): void {
    this.tabelaIbptService.obterSituacao().subscribe({
      next: (situacaoTabelaIbptResponse: SituacaoTabelaIbptResponse) => {
        if (this.isTelaEncerrada) {
          return;
        }

        if (situacaoTabelaIbptResponse.isError) {
          this.pararConsultaSituacao();
          this.alertService.show('Erro', situacaoTabelaIbptResponse.errorDescription, AlertType.error);
          return;
        }

        situacaoTabelaIbptResponse.datetimeUltimaAtualizacao = this.lerDataUtc(situacaoTabelaIbptResponse.datetimeUltimaAtualizacao);
        situacaoTabelaIbptResponse.datetimeUltimaVerificacao = this.lerDataUtc(situacaoTabelaIbptResponse.datetimeUltimaVerificacao);
        situacaoTabelaIbptResponse.datetimeUltimaVerificacaoSucesso = this.lerDataUtc(situacaoTabelaIbptResponse.datetimeUltimaVerificacaoSucesso);
        situacaoTabelaIbptResponse.datetimeProximaVerificacao = this.lerDataUtc(situacaoTabelaIbptResponse.datetimeProximaVerificacao);
        if (situacaoTabelaIbptResponse.execucaoEmAndamento) {
          situacaoTabelaIbptResponse.execucaoEmAndamento.datetimeInicio = this.lerDataUtc(situacaoTabelaIbptResponse.execucaoEmAndamento.datetimeInicio) as Date;
          situacaoTabelaIbptResponse.execucaoEmAndamento.datetimeProximaTentativa = this.lerDataUtc(situacaoTabelaIbptResponse.execucaoEmAndamento.datetimeProximaTentativa);
        }
        if (situacaoTabelaIbptResponse.avisoFalha) {
          situacaoTabelaIbptResponse.avisoFalha.datetimeAlerta = this.lerDataUtc(situacaoTabelaIbptResponse.avisoFalha.datetimeAlerta);
          situacaoTabelaIbptResponse.avisoFalha.listaDatetimeTentativa = situacaoTabelaIbptResponse.avisoFalha.listaDatetimeTentativa
            .map(d => this.lerDataUtc(d) as Date);
        }
        if (situacaoTabelaIbptResponse.ultimaExecucao) {
          situacaoTabelaIbptResponse.ultimaExecucao.datetimeFim = this.lerDataUtc(situacaoTabelaIbptResponse.ultimaExecucao.datetimeFim);
        }
        this.situacao = situacaoTabelaIbptResponse;
        this.montarMapaUf();
        this.diasParaVencer = this.calcularDiasParaVencer(situacaoTabelaIbptResponse.dataVigenciaFim);

        if (this.isRolarHistoricoAoCarregar) {
          this.isRolarHistoricoAoCarregar = false;
          setTimeout(() => this.rolarAteHistorico());
        }

        if (this.isAbrirImportacaoAoCarregar) {
          this.isAbrirImportacaoAoCarregar = false;
          const isPodeImportar = (this.authService.getUserInfos()?.listaPermissoes ?? []).includes(
            PermissaoEnum.Desenvolvedor_TabelaIbpt_Incluir
          );
          if (isPodeImportar && !this.isEmAndamento) {
            this.abrirImportacao();
          }
        }

        if (this.isEmAndamento) {
          if (situacaoTabelaIbptResponse.execucaoEmAndamento) {
            this.idIbptExecucaoAcompanhada = situacaoTabelaIbptResponse.execucaoEmAndamento.idIbptExecucao;
          }

          if (!this.intervaloSituacao) {
            this.intervaloSituacao = setInterval(() => this.obterSituacao(), 10000);
          }
          return;
        }

        this.pararConsultaSituacao();
        const ultimaExecucao = situacaoTabelaIbptResponse.ultimaExecucao;
        if (this.idIbptExecucaoAcompanhada !== null
          && ultimaExecucao
          && ultimaExecucao.idIbptExecucao === this.idIbptExecucaoAcompanhada
          && ultimaExecucao.ibptOrigemExecucao === IbptOrigemExecucaoEnum.Api
          && !ultimaExecucao.isAgendada) {
          this.mostrarMensagemFinal();
          // O worker atualiza o status da conexão em cada consulta ao IBPT.
          this.obterConfiguracao();
          this.recarregarHistorico();
        }

        this.idIbptExecucaoAcompanhada = null;
      },
      error: (error: any) => {
        this.pararConsultaSituacao();
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  // Mapa do card a partir dos ids da situação, com as siglas do lookup (o mapa é desenhado por sigla). Sem a situação
  // ou sem o lookup, o mapa fica sem cores.
  private montarMapaUf(): void {
    const situacao = this.situacao;
    if (!situacao) {
      return;
    }

    // Sem execução: verde para as UFs carregadas; as demais ficam "Sem tabela".
    // Em andamento: a carga consulta as UFs na ordem dos ids, então as anteriores à UF atual já foram consultadas;
    // Repetindo itens com erro e Gravando vêm depois da consulta de todas as UFs. As UFs já carregadas ficam verdes
    // em qualquer etapa (Atualização solicitada, Verificando versão e Aguardando nova tentativa mostram só elas).
    const listaIdUnidadeFederativaCarregada = situacao.listaIdUnidadeFederativaCarregada;
    const execucaoEmAndamento = situacao.execucaoEmAndamento;
    const idEtapa = execucaoEmAndamento?.ibptEtapaExecucao;
    const idUfAtual =
      idEtapa === IbptEtapaExecucaoEnum.ConsultandoItens
        ? (execucaoEmAndamento?.idUnidadeFederativaAtual ?? null)
        : null;
    const isConsultaConcluida =
      idEtapa === IbptEtapaExecucaoEnum.RepetindoItensComErro || idEtapa === IbptEtapaExecucaoEnum.Gravando;
    const situacaoPorUfCarregada: Record<string, 'carregada'> = {};
    const situacaoPorUfAndamento: Record<string, 'consultada' | 'emConsulta'> = {};
    this.listaUnidadeFederativa.forEach(unidadeFederativa => {
      const idUf = unidadeFederativa.idUnidadeFederativa;
      const sigla = unidadeFederativa.uf;
      const isCarregada = listaIdUnidadeFederativaCarregada.includes(idUf);
      if (isCarregada) {
        situacaoPorUfCarregada[sigla] = 'carregada';
      }

      if (idUfAtual !== null && idUf === idUfAtual) {
        situacaoPorUfAndamento[sigla] = 'emConsulta';
      } else if (isConsultaConcluida || isCarregada || (idUfAtual !== null && idUf < idUfAtual)) {
        situacaoPorUfAndamento[sigla] = 'consultada';
      }
    });
    this.situacaoPorUfCarregada = situacaoPorUfCarregada;
    this.situacaoPorUfAndamento = situacaoPorUfAndamento;
  }

  private pararConsultaSituacao(): void {
    if (this.intervaloSituacao) {
      clearInterval(this.intervaloSituacao);
      this.intervaloSituacao = null;
    }
  }

  // Mensagem da execução manual que a tela acompanhou ("Atualizar agora" ou "Tentar novamente").
  private mostrarMensagemFinal(): void {
    const ultimaExecucao = this.situacao?.ultimaExecucao;
    if (!ultimaExecucao) {
      return;
    }

    switch (ultimaExecucao.ibptResultadoExecucao) {
      case IbptResultadoExecucaoEnum.SemAlteracao:
        this.alertService.show('Sucesso', 'Verificação concluída. A versão ' + ultimaExecucao.versao + ' já estava carregada.', AlertType.success);
        break;
      case IbptResultadoExecucaoEnum.Sucesso:
        this.alertService.show('Sucesso', 'Tabela atualizada para a versão ' + ultimaExecucao.versao + '.', AlertType.success);
        break;
      case IbptResultadoExecucaoEnum.Falha:
        if (ultimaExecucao.ibptMotivoFalhaExecucao === IbptMotivoFalhaExecucaoEnum.CredenciaisRecusadas) {
          this.alertService.show('Erro', 'O IBPT recusou o CNPJ e o token. Não foram feitas novas tentativas.', AlertType.error);
        } else {
          this.alertService.show('Erro', 'A API não respondeu após 3 tentativas.', AlertType.error);
        }
        break;
      case IbptResultadoExecucaoEnum.Cancelada:
        this.alertService.show('Aviso', 'Atualização cancelada.', AlertType.info);
        break;
    }
  }

  // A API só grava a execução na fila; o worker a pega em até 1 minuto. A situação é recarregada na hora e mostra
  // "Atualização solicitada" enquanto a execução não tem etapa.
  public solicitarAtualizacao(): void {
    if (this.isSolicitandoAtualizacao || this.isEmAndamento) {
      return;
    }

    this.isSolicitandoAtualizacao = true;
    this.tabelaIbptService.solicitarAtualizacao().subscribe({
      next: (returnStruct: ReturnStruct) => {
        this.isSolicitandoAtualizacao = false;
        if (returnStruct.isError) {
          this.alertService.show('Erro', returnStruct.errorDescription, AlertType.error);
          return;
        }

        this.obterSituacao();
        this.recarregarHistorico();
        this.alertService.show('Sucesso', 'Atualização solicitada. Ela começa em até 1 minuto e você pode sair desta tela.', AlertType.success);
      },
      error: (error: any) => {
        this.isSolicitandoAtualizacao = false;
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  // "Cancelar atualização": a API só grava o pedido; o worker encerra a execução como Cancelada e mantém o progresso.
  // A situação recarregada mostra "Cancelando…" até a execução terminar.
  public confirmarCancelamentoExecucao(): void {
    const execucaoEmAndamento = this.situacao?.execucaoEmAndamento;
    if (!execucaoEmAndamento || execucaoEmAndamento.isCancelamentoSolicitado || this.isSolicitandoCancelamento) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      width: '500px',
      data: {
        title: 'Cancelar a atualização?',
        description: 'O que já foi consultado fica guardado e a próxima atualização continua de onde parou.',
        buttonConfirm: 'Cancelar atualização',
        buttonClose: 'Voltar',
      },
    });

    dialogRef.afterClosed().subscribe({
      next: result => {
        if (result && result.confirm) {
          this.cancelarExecucao(execucaoEmAndamento.idIbptExecucao);
        }
      },
    });
  }

  private cancelarExecucao(idIbptExecucao: number): void {
    this.isSolicitandoCancelamento = true;
    this.tabelaIbptService.cancelarExecucao(idIbptExecucao).subscribe({
      next: (returnStruct: ReturnStruct) => {
        this.isSolicitandoCancelamento = false;
        if (returnStruct.isError) {
          this.alertService.show('Erro', returnStruct.errorDescription, AlertType.error);
          return;
        }

        this.obterSituacao();
      },
      error: (error: any) => {
        this.isSolicitandoCancelamento = false;
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  public testarConexao(): void {
    if (this.isTestandoConexao) {
      return;
    }

    this.isTestandoConexao = true;
    this.tabelaIbptService.testarConexao().subscribe({
      next: (testarConexaoIbptResponse: TestarConexaoIbptResponse) => {
        this.isTestandoConexao = false;
        if (testarConexaoIbptResponse.isError) {
          this.alertService.show('Erro', testarConexaoIbptResponse.errorDescription, AlertType.error);
          return;
        }

        if (this.configuracao) {
          this.configuracao.ibptStatusConexao = testarConexaoIbptResponse.ibptStatusConexao;
          this.configuracao.datetimeUltimoTeste = this.lerDataUtc(testarConexaoIbptResponse.datetimeUltimoTeste);
        }

        switch (testarConexaoIbptResponse.ibptStatusConexao) {
          case IbptStatusConexaoEnum.Conectada:
            this.alertService.show('Sucesso', 'Conexão com a API do IBPT funcionando.', AlertType.success);
            break;
          case IbptStatusConexaoEnum.CredenciaisRecusadas:
            this.alertService.show('Erro', 'O IBPT recusou o CNPJ e o token atuais.', AlertType.error);
            break;
          default:
            this.alertService.show('Erro', 'A API do IBPT não respondeu ao teste.', AlertType.error);
            break;
        }
      },
      error: (error: any) => {
        this.isTestandoConexao = false;
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  public abrirAlterarCredencial(): void {
    const data: AlterarCredencialIbptDialogData = {
      cnpj: this.configuracao?.cnpj ?? null,
      finalToken: this.configuracao?.finalToken ?? null,
    };

    const dialogRef = this.dialog.open(AlterarCredencialIbptDialogComponent, {
      width: '500px',
      data,
    });

    dialogRef.afterClosed().subscribe({
      next: (isSalvo: boolean | undefined) => {
        if (!isSalvo) {
          return;
        }

        this.alertService.show('Sucesso', 'CNPJ e token testados e salvos.', AlertType.success);
        this.obterConfiguracao();
      },
    });
  }

  public abrirDestinatario(): void {
    const data: DestinatarioIbptDialogData = {
      listaEmail: this.configuracao?.listaEmailDestinatario ?? [],
      isAvisarRetorno: this.configuracao?.isAvisarRetorno ?? false,
    };

    const dialogRef = this.dialog.open(DestinatarioIbptDialogComponent, {
      width: '500px',
      data,
    });

    dialogRef.afterClosed().subscribe({
      next: (isSalvo: boolean | undefined) => {
        if (!isSalvo) {
          return;
        }

        this.alertService.show('Sucesso', 'Destinatários salvos.', AlertType.success);
        this.obterConfiguracao();
      },
    });
  }

  // "Importar planilha": bloqueada com execução em andamento (seção 1 da história).
  public abrirImportacao(): void {
    if (this.isEmAndamento) {
      return;
    }

    const dialogRef = this.dialog.open<ImportarPlanilhaIbptDialogComponent, void, ImportarPlanilhaIbptDialogResult>(
      ImportarPlanilhaIbptDialogComponent,
      {
        width: '1100px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        // Arquivos escolhidos não se perdem num clique fora; o diálogo fecha por Cancelar ou pelo X.
        disableClose: true,
        autoFocus: false,
      }
    );

    dialogRef.afterClosed().subscribe({
      next: (resultado: ImportarPlanilhaIbptDialogResult | undefined) => {
        if (!resultado?.isConcluido) {
          return;
        }

        this.alertService.show('Sucesso', 'Importação concluída.', AlertType.success);
        this.obterSituacao();
        this.obterConfiguracao();
        this.recarregarHistorico();
        if (resultado.isVerHistorico) {
          setTimeout(() => this.rolarAteHistorico());
        }
      },
    });
  }

  // Olho do histórico e link do e-mail. O detalhe e a duração saem das mesmas funções do grid.
  public abrirDetalheExecucao(idIbptExecucao: number): void {
    if (this.idIbptExecucaoCarregandoDetalhe !== null) {
      return;
    }

    this.idIbptExecucaoCarregandoDetalhe = idIbptExecucao;
    this.tabelaIbptService.obterDetalheExecucao(idIbptExecucao).subscribe({
      next: (detalheExecucaoIbptResponse: DetalheExecucaoIbptResponse) => {
        this.idIbptExecucaoCarregandoDetalhe = null;
        const execucao = detalheExecucaoIbptResponse.execucao;
        if (detalheExecucaoIbptResponse.isError || !execucao) {
          this.alertService.show('Erro', detalheExecucaoIbptResponse.errorDescription, AlertType.error);
          return;
        }

        execucao.datetimeInicio = this.lerDataUtc(execucao.datetimeInicio) as Date;
        execucao.datetimeFim = this.lerDataUtc(execucao.datetimeFim);
        detalheExecucaoIbptResponse.listaTentativa.forEach(tentativa => {
          tentativa.datetimeInicio = this.lerDataUtc(tentativa.datetimeInicio) as Date;
          tentativa.datetimeFim = this.lerDataUtc(tentativa.datetimeFim);
        });
        detalheExecucaoIbptResponse.listaLog.forEach(log => {
          log.datetimeInclusao = this.lerDataUtc(log.datetimeInclusao) as Date;
        });

        const data: DetalheExecucaoIbptDialogData = {
          detalheExecucao: detalheExecucaoIbptResponse,
          detalhe: this.montarDetalhe(execucao),
          duracao: this.formatarDuracao(execucao),
          listaUnidadeFederativa: this.listaUnidadeFederativa,
        };

        this.dialog.open(DetalheExecucaoIbptDialogComponent, {
          width: '900px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          autoFocus: false,
          data,
        });
      },
      error: (error: any) => {
        this.idIbptExecucaoCarregandoDetalhe = null;
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  private rolarAteHistorico(): void {
    document.getElementById('historico')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  public pesquisar(): void {
    this.quantidadeDiasAplicado = this.filtroPeriodo;
    this.origemAplicada = this.filtroOrigem;
    this.resultadoAplicado = this.filtroResultado;
    this.recarregarHistorico();
  }

  // Volta à página 0 com os filtros já aplicados, sem copiar os da tela.
  private recarregarHistorico(): void {
    this.paginaAtual = 0;
    this.listarExecucao();
  }

  public verMais(): void {
    this.paginaAtual = this.paginaAtual + 1;
    this.listarExecucao();
  }

  private listarExecucao(): void {
    const isPrimeiraPagina = this.paginaAtual === 0;
    this.isCarregandoHistorico = true;
    this.tabelaIbptService
      .listarExecucao(this.quantidadeDiasAplicado, this.origemAplicada, this.resultadoAplicado, this.paginaAtual)
      .subscribe({
        next: (listaExecucaoIbptResponse: ListaExecucaoIbptResponse) => {
          this.isCarregandoHistorico = false;
          if (listaExecucaoIbptResponse.isError) {
            if (!isPrimeiraPagina) {
              this.paginaAtual = this.paginaAtual - 1;
            }
            this.alertService.show('Erro', listaExecucaoIbptResponse.errorDescription, AlertType.error);
            return;
          }

          listaExecucaoIbptResponse.listaExecucao.forEach(execucao => {
            execucao.datetimeInicio = this.lerDataUtc(execucao.datetimeInicio) as Date;
            execucao.datetimeFim = this.lerDataUtc(execucao.datetimeFim);
          });
          this.listaExecucaoExibida = isPrimeiraPagina
            ? listaExecucaoIbptResponse.listaExecucao
            : [...this.listaExecucaoExibida, ...listaExecucaoIbptResponse.listaExecucao];
          this.isUltimaPagina = listaExecucaoIbptResponse.isUltimaPagina;
        },
        error: (error: any) => {
          this.isCarregandoHistorico = false;
          if (!isPrimeiraPagina) {
            this.paginaAtual = this.paginaAtual - 1;
          }
          this.alertService.show('Erro inesperado', error.message, AlertType.error);
        },
      });
  }

  public get isEmAndamento(): boolean {
    return this.situacao?.ibptStatusTabela === IbptStatusTabelaEnum.Atualizando;
  }

  public get percentualProgresso(): number {
    const execucao = this.situacao?.execucaoEmAndamento;
    if (!execucao || execucao.quantidadeItensTotal <= 0) {
      return 0;
    }

    return Math.round(execucao.quantidadeItensProcessados / execucao.quantidadeItensTotal * 100);
  }

  public get tituloAvisoFalha(): string {
    switch (this.situacao?.avisoFalha?.ibptMotivoFalhaExecucao) {
      case IbptMotivoFalhaExecucaoEnum.SemRespostaApi:
        return 'A API do IBPT não respondeu na verificação de hoje';
      case IbptMotivoFalhaExecucaoEnum.CredenciaisRecusadas:
        return 'O IBPT recusou o token na verificação de hoje';
      default:
        return 'A atualização da Tabela IBPT falhou na verificação de hoje';
    }
  }

  public get textoVencimento(): string {
    if (this.diasParaVencer === null) {
      return '—';
    }

    if (this.diasParaVencer < 0) {
      const dias = -this.diasParaVencer;
      return 'vencida há ' + dias + (dias === 1 ? ' dia' : ' dias');
    }

    if (this.diasParaVencer === 0) {
      return 'vence hoje';
    }

    return 'vence em ' + this.diasParaVencer + (this.diasParaVencer === 1 ? ' dia' : ' dias');
  }

  public descricaoMotivoFalha(idMotivo: IbptMotivoFalhaExecucaoEnum | null): string {
    switch (idMotivo) {
      case IbptMotivoFalhaExecucaoEnum.SemRespostaApi:
        return 'tempo esgotado';
      case IbptMotivoFalhaExecucaoEnum.CredenciaisRecusadas:
        return 'credenciais recusadas pelo IBPT';
      case IbptMotivoFalhaExecucaoEnum.ItensComErroAcimaLimite:
        return 'itens com erro acima do limite';
      case IbptMotivoFalhaExecucaoEnum.ErroGravacao:
        return 'erro na gravação';
      default:
        return 'erro inesperado';
    }
  }

  public descricaoEtapa(idEtapa: IbptEtapaExecucaoEnum | null): string {
    switch (idEtapa) {
      case null:
        return 'Atualização solicitada';
      case IbptEtapaExecucaoEnum.VerificandoVersao:
        return 'Verificando versão';
      case IbptEtapaExecucaoEnum.ConsultandoItens:
        return 'Consultando itens';
      case IbptEtapaExecucaoEnum.RepetindoItensComErro:
        return 'Repetindo itens com erro';
      case IbptEtapaExecucaoEnum.AguardandoNovaTentativa:
        return 'Aguardando nova tentativa';
      default:
        return 'Gravando';
    }
  }

  public descricaoOrigem(idOrigem: IbptOrigemExecucaoEnum | null): string {
    return idOrigem === IbptOrigemExecucaoEnum.Planilha ? 'Planilha' : 'API';
  }

  public descricaoResultado(idResultado: IbptResultadoExecucaoEnum): string {
    switch (idResultado) {
      case IbptResultadoExecucaoEnum.EmAndamento:
        return 'Em andamento';
      case IbptResultadoExecucaoEnum.Sucesso:
        return 'Sucesso';
      case IbptResultadoExecucaoEnum.SemAlteracao:
        return 'Sem alteração';
      case IbptResultadoExecucaoEnum.Cancelada:
        return 'Cancelada';
      default:
        return 'Falha';
    }
  }

  public iconeResultado(idResultado: IbptResultadoExecucaoEnum): string {
    switch (idResultado) {
      case IbptResultadoExecucaoEnum.EmAndamento:
        return 'sync';
      case IbptResultadoExecucaoEnum.Sucesso:
        return 'check';
      case IbptResultadoExecucaoEnum.SemAlteracao:
        return 'remove';
      case IbptResultadoExecucaoEnum.Cancelada:
        return 'block';
      default:
        return 'close';
    }
  }

  // O texto do histórico é montado pela tela a partir das contagens e da comparação com a versão anterior;
  // o banco não guarda o texto pronto.
  public montarDetalhe(execucao: ExecucaoIbptStruct): string {
    switch (execucao.ibptResultadoExecucao) {
      case IbptResultadoExecucaoEnum.EmAndamento:
        return this.formatarNumero(execucao.quantidadeItensProcessados) + ' de '
          + this.formatarNumero(execucao.quantidadeItensTotal) + ' itens';
      case IbptResultadoExecucaoEnum.SemAlteracao:
        return 'Versão já carregada';
      case IbptResultadoExecucaoEnum.Cancelada:
        return 'Cancelada pelo usuário';
      case IbptResultadoExecucaoEnum.Falha:
        if (execucao.ibptMotivoFalhaExecucao === IbptMotivoFalhaExecucaoEnum.CredenciaisRecusadas) {
          return 'Credenciais recusadas pelo IBPT';
        }

        if (execucao.ibptMotivoFalhaExecucao === IbptMotivoFalhaExecucaoEnum.SemRespostaApi) {
          return 'Tempo esgotado após ' + (execucao.numeroTentativa ?? 3) + ' tentativas';
        }

        return this.descricaoMotivoFalha(execucao.ibptMotivoFalhaExecucao);
      default:
        if (execucao.ibptOrigemExecucao === IbptOrigemExecucaoEnum.Planilha) {
          const quantidadeLinhas = execucao.listaArquivo.reduce(
            (total, a) => total + a.quantidadeLinhasValidas + a.quantidadeLinhasRejeitadas, 0);
          const quantidadeArquivos = execucao.listaArquivo.length;
          return quantidadeArquivos + (quantidadeArquivos === 1 ? ' arquivo (' : ' arquivos (')
            + execucao.listaArquivo.map(a => this.siglaUf(a.idUnidadeFederativa)).join(', ') + ') · '
            + this.formatarNumero(quantidadeLinhas) + ' linhas';
        }

        const detalheConsulta = this.formatarNumero(execucao.quantidadeConsultados) + ' consultados · '
          + this.formatarNumero(execucao.quantidadeAtualizados) + ' atualizados';
        const isRetificacao = execucao.versaoAnterior !== null
          && execucao.dataVigenciaInicio !== null
          && execucao.dataVigenciaInicioAnterior !== null
          && new Date(execucao.dataVigenciaInicio).getTime() === new Date(execucao.dataVigenciaInicioAnterior).getTime();
        return isRetificacao
          ? 'Retificação da versão ' + execucao.versaoAnterior + ' · ' + detalheConsulta
          : detalheConsulta;
    }
  }

  public formatarDuracao(execucao: ExecucaoIbptStruct): string {
    if (!execucao.datetimeFim) {
      return '—';
    }

    const segundos = Math.max(1, Math.round((new Date(execucao.datetimeFim).getTime() - new Date(execucao.datetimeInicio).getTime()) / 1000));
    if (segundos < 60) {
      return segundos + ' s';
    }

    return Math.floor(segundos / 60) + ' min ' + (segundos % 60) + ' s';
  }

  // A API grava e devolve os horários em UTC, nem sempre com o "Z" no fim. Mesmo padrão das telas de fabricante:
  // acrescenta o "Z" para o navegador converter para o horário local.
  private lerDataUtc(valor: Date | string | null | undefined): Date | null {
    if (!valor) {
      return null;
    }

    if (valor instanceof Date) {
      return valor;
    }

    const texto = valor.toString();
    return new Date(/(Z|[+-]\d{2}:\d{2})$/.test(texto) ? texto : texto + 'Z');
  }

  private formatarNumero(valor: number): string {
    return valor.toLocaleString('pt-BR');
  }

  // CNPJ numérico ou alfanumérico (12 posições letras/números + 2 dígitos verificadores): AA.AAA.AAA/AAAA-00.
  private formatarCnpj(cnpj: string | null): string {
    if (!cnpj || cnpj.length !== 14) {
      return cnpj ?? '';
    }

    return cnpj.substring(0, 2) + '.' + cnpj.substring(2, 5) + '.' + cnpj.substring(5, 8) + '/'
      + cnpj.substring(8, 12) + '-' + cnpj.substring(12);
  }

  // A API devolve a data como texto (aaaa-mm-dd, com ou sem hora zerada). Só a parte da data é lida, no fuso local:
  // new Date só com aaaa-mm-dd leria em UTC e voltaria um dia no horário de Brasília.
  private calcularDiasParaVencer(dataVigenciaFim: Date | string | null): number | null {
    if (!dataVigenciaFim) {
      return null;
    }

    const hoje = new Date();
    const inicioHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
    const fim = typeof dataVigenciaFim === 'string'
      ? new Date(dataVigenciaFim.substring(0, 10) + 'T00:00:00')
      : new Date(dataVigenciaFim.getFullYear(), dataVigenciaFim.getMonth(), dataVigenciaFim.getDate());
    return Math.round((fim.getTime() - inicioHoje.getTime()) / 86400000);
  }
}
